import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Command,
  GripVertical,
  Info,
  Monitor,
  Moon,
  Palette,
  Puzzle,
  RefreshCcw,
  RotateCcw,
  Save as SaveIcon,
  Sparkles,
  Sun,
  TerminalSquare,
  WrapText,
  X,
} from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import { isTauriEnv } from './useTauriEnv';
import {
  AUTOSAVE_DEBOUNCE_MAX_MS,
  AUTOSAVE_DEBOUNCE_MIN_MS,
  AUTOSAVE_MAX_WAIT_CEILING_MS,
  PALETTE_MIN_WORKSPACE_QUERY_MAX,
  PALETTE_MIN_WORKSPACE_QUERY_MIN,
  PALETTE_MIXED_CAP_MAX,
  PALETTE_MIXED_CAP_MIN,
  PALETTE_WORKSPACE_DEBOUNCE_MAX_MS,
  PALETTE_WORKSPACE_DEBOUNCE_MIN_MS,
  THEME_NAMES,
  TERMINAL_CURSOR_STYLES,
  useDocStore,
  type TerminalCursorStyle,
  type ThemeName,
  type ThemePref,
} from '../store/docStore';
import { useUpdateStore } from '../store/updateStore';
import { LANGUAGE_CHOICES, languageChoice } from '../util/docLanguage';
import { PluginsSettings } from '../plugins/PluginsSettings';
import { getAppPluginSystem } from '../plugins/appPlugins';

export interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional platform bridge. When provided, the About category fetches the
   * running app version via `platform.getAppVersion()`. Threaded down from
   * App so the panel can stay platform-agnostic but show the live version.
   */
  platform?: PlatformBridge;
}

/**
 * App-wide preferences. A Mac-style "Settings" panel with a category sidebar
 * on the left and per-category controls on the right. The first category,
 * Appearance, holds the typography theme + color-mode controls that used to
 * live as standalone buttons in the title bar.
 *
 * The component is designed to grow: each entry in `CATEGORIES` is a card-
 * sized `{ id, label, icon, body }` record; later phases slot new categories
 * in by extending the array — no new shell wiring needed.
 *
 * Renders into a portal at `document.body` so it escapes overflow/transform
 * ancestors and sits above every other surface. Focus is trapped to the
 * panel while open; Esc / backdrop / Close all dismiss.
 */
export function SettingsDialog({ open, onClose, platform }: SettingsDialogProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocus = useRef<Element | null>(null);
  const titleId = useId();
  // Persist the last-visited category so reopening lands on it.
  const [activeId, setActiveId] = useState<string>(() => loadLastCategory() ?? 'appearance');
  // About is web-only: on desktop the OS menu already opens the rich
  // AboutDialog (Acknowledgements, etc.). The web has no menu so Settings
  // hosts the same info inline.
  // Updates and About are environment-gated. Desktop has the OS-menu About
  // (richer, with Acknowledgements) and the in-app updater; web has neither
  // updater nor menu, so it surfaces About in Settings instead.
  const showAbout = !isTauriEnv();
  const showUpdates = isTauriEnv();
  const categories = CATEGORIES.filter((c) => {
    if (c.id === 'about') return showAbout;
    if (c.id === 'updates') return showUpdates;
    return true;
  });

  // Focus the panel on open, restore the previous focus on close.
  useEffect(() => {
    if (!open) return;
    restoreFocus.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const el = restoreFocus.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, [open]);

  // Dismiss on Escape.
  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  // Persist category on change.
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(CATEGORY_KEY, activeId);
    } catch {
      /* private-mode safari etc. — silently ignore */
    }
  }, [activeId]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const active = categories.find((c) => c.id === activeId) ?? categories[0];
  const ActiveBody = active.body;

  return createPortal(
    <div className="mp-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="mp-modal mp-settings"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="mp-settings__shell">
          <aside className="mp-settings__sidebar" aria-label="Settings categories">
            <h2 id={titleId} className="mp-settings__title">Settings</h2>
            <ul className="mp-settings__nav" role="tablist" aria-orientation="vertical">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const selected = cat.id === active.id;
                return (
                  <li key={cat.id} role="none">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      tabIndex={selected ? 0 : -1}
                      className="mp-settings__nav-item"
                      data-active={selected ? '' : undefined}
                      onClick={() => setActiveId(cat.id)}
                    >
                      <Icon size={15} aria-hidden />
                      <span>{cat.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
          <section className="mp-settings__panel" role="tabpanel" aria-labelledby={titleId}>
            <header className="mp-settings__panel-header">
              <h3 className="mp-settings__panel-title">{active.label}</h3>
              <button
                type="button"
                className="mp-settings__close"
                onClick={onClose}
                aria-label="Close settings"
                title="Close (Esc)"
              >
                <X size={15} strokeWidth={2.25} aria-hidden />
              </button>
            </header>
            <div className="mp-settings__panel-body">
              <ActiveBody platform={platform} />
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ───────── Categories ─────────

interface PanelProps {
  platform?: PlatformBridge;
}

interface Category {
  id: string;
  label: string;
  icon: typeof Palette;
  body: (props: PanelProps) => ReactElement;
}

const CATEGORIES: Category[] = [
  {
    id: 'appearance',
    label: 'Appearance',
    icon: Palette,
    body: AppearanceSettings,
  },
  {
    id: 'save',
    label: 'Save',
    icon: SaveIcon,
    body: SaveSettings,
  },
  {
    id: 'editor',
    label: 'Editor',
    icon: WrapText,
    body: EditorSettings,
  },
  {
    id: 'palette',
    label: 'Command Palette',
    icon: Command,
    body: PaletteSettings,
  },
  {
    id: 'terminal',
    label: 'Terminal',
    icon: TerminalSquare,
    body: TerminalSettings,
  },
  {
    id: 'plugins',
    label: 'Plugins',
    icon: Puzzle,
    body: PluginsSettingsPanel,
  },
  {
    id: 'updates',
    label: 'Updates',
    icon: Sparkles,
    body: UpdatesSettings,
  },
  {
    id: 'about',
    label: 'About',
    icon: Info,
    body: AboutPanel,
  },
];

const CATEGORY_KEY = 'slate:settings:category';

function loadLastCategory(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(CATEGORY_KEY);
  } catch {
    return null;
  }
}

// ───────── Appearance panel ─────────

const COLOR_MODES: { pref: ThemePref; label: string; icon: typeof Sun }[] = [
  { pref: 'light', label: 'Light', icon: Sun },
  { pref: 'system', label: 'System', icon: Monitor },
  { pref: 'dark', label: 'Dark', icon: Moon },
];

const THEME_LABELS: Record<ThemeName, { group: string; variant: string }> = {
  'sans-contrast': { group: 'Sans', variant: 'High contrast' },
  'sans-reader': { group: 'Sans', variant: 'Reader' },
  'serif-contrast': { group: 'Serif', variant: 'High contrast' },
  'serif-reader': { group: 'Serif', variant: 'Reader' },
};

const SANS_FONT =
  "'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const SERIF_FONT =
  "'Source Serif 4 Variable', 'Charter', 'Iowan Old Style', Georgia, serif";

type SampleStyle = CSSProperties & { '--mp-sample-font': string };

function AppearanceSettings(_: PanelProps): ReactElement {
  const theme = useDocStore((s) => s.theme);
  const setTheme = useDocStore((s) => s.setTheme);
  const themePref = useDocStore((s) => s.themePref);
  const setThemePref = useDocStore((s) => s.setThemePref);

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Color mode</h4>
          <p>Switch between light, dark, or follow the system.</p>
        </div>
        <div className="mp-settings__radio-row" role="radiogroup" aria-label="Color mode">
          {COLOR_MODES.map(({ pref, label, icon: Icon }) => {
            const selected = themePref === pref;
            return (
              <button
                key={pref}
                type="button"
                role="radio"
                aria-checked={selected}
                className="mp-settings__radio-card"
                data-active={selected ? '' : undefined}
                onClick={() => setThemePref(pref)}
              >
                <Icon size={18} aria-hidden />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Typography theme</h4>
          <p>
            Two prose typefaces × two density modes. The high-contrast variants are
            crisp for editing; readers are warmer and easier on the eyes.
          </p>
        </div>
        <div className="mp-settings__theme-grid" role="radiogroup" aria-label="Typography theme">
          {THEME_NAMES.map((name) => {
            const { group, variant } = THEME_LABELS[name];
            const font = group === 'Sans' ? SANS_FONT : SERIF_FONT;
            const selected = theme === name;
            return (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={selected}
                className="mp-settings__theme-card"
                data-active={selected ? '' : undefined}
                data-theme-name={name}
                onClick={() => setTheme(name)}
              >
                <span
                  className="mp-settings__theme-sample"
                  data-theme-name={name}
                  style={{ '--mp-sample-font': font } as SampleStyle}
                >
                  <span className="mp-settings__theme-sample-heading">{group}</span>
                  <span className="mp-settings__theme-sample-prose">
                    The quick brown fox.
                  </span>
                </span>
                <span className="mp-settings__theme-meta">
                  <span className="mp-settings__theme-name">{group}</span>
                  <span className="mp-settings__theme-variant">{variant}</span>
                </span>
                {selected && (
                  <span className="mp-settings__theme-check" aria-hidden>
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ───────── Save panel ─────────

/**
 * Save preferences. Two controls today:
 *   - Autosave on/off (file-backed dirty docs only).
 *   - Debounce + max-wait windows (ms), revealed only when autosave is on.
 *
 * The debounce is the idle window: a save fires this long after the last edit.
 * Max-wait is the ceiling during continuous editing — a doc never stays dirty
 * for longer than this. Both clamp on commit (blur / Enter); see docStore
 * setters for the bounds.
 */
function SaveSettings(_: PanelProps): ReactElement {
  const autosave = useDocStore((s) => s.autosave);
  const setAutosave = useDocStore((s) => s.setAutosave);
  const debounceMs = useDocStore((s) => s.autosaveDebounceMs);
  const setDebounceMs = useDocStore((s) => s.setAutosaveDebounceMs);
  const maxWaitMs = useDocStore((s) => s.autosaveMaxWaitMs);
  const setMaxWaitMs = useDocStore((s) => s.setAutosaveMaxWaitMs);

  const toggleId = useId();

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Autosave</h4>
          <p>
            Save file-backed documents in the background after a short idle window.
            Untitled drafts always need an explicit Save As — they’re never written silently.
          </p>
        </div>
        <div className="mp-settings__row">
          <label htmlFor={toggleId} className="mp-settings__row-label">
            Autosave changes
          </label>
          <button
            id={toggleId}
            type="button"
            role="switch"
            aria-checked={autosave}
            className="mp-switch"
            data-on={autosave ? '' : undefined}
            onClick={() => setAutosave(!autosave)}
          >
            <span className="mp-switch__track" aria-hidden>
              <span className="mp-switch__thumb" />
            </span>
            <span className="mp-switch__label" aria-hidden>
              {autosave ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      </section>

      {autosave && (
        <section className="mp-settings__section">
          <div className="mp-settings__section-head">
            <h4>Timing</h4>
            <p>
              Tune how aggressively edits land on disk. Lower values save more often;
              higher values batch keystrokes into fewer writes.
            </p>
          </div>
          <DurationField
            label="Idle debounce"
            help="Save after this long without keystrokes."
            valueMs={debounceMs}
            minMs={AUTOSAVE_DEBOUNCE_MIN_MS}
            maxMs={AUTOSAVE_DEBOUNCE_MAX_MS}
            onCommit={setDebounceMs}
          />
          <DurationField
            label="Max wait"
            help="During continuous editing, force a save by this deadline."
            valueMs={maxWaitMs}
            minMs={debounceMs}
            maxMs={AUTOSAVE_MAX_WAIT_CEILING_MS}
            onCommit={setMaxWaitMs}
          />
        </section>
      )}
    </div>
  );
}

// ───────── Editor panel ─────────

/**
 * Editor preferences — soft-wrap for the plaintext / code editor:
 *   - Global default on/off.
 *   - "Don't wrap code files" — keeps non-prose languages on single long lines.
 *   - Per-language exceptions — a user-managed list that overrides both rules.
 */
function EditorSettings(_: PanelProps): ReactElement {
  const softWrapDefault = useDocStore((s) => s.softWrapDefault);
  const setSoftWrapDefault = useDocStore((s) => s.setSoftWrapDefault);
  const excludeCode = useDocStore((s) => s.softWrapExcludeCode);
  const setExcludeCode = useDocStore((s) => s.setSoftWrapExcludeCode);
  const byLanguage = useDocStore((s) => s.softWrapByLanguage);
  const setLanguage = useDocStore((s) => s.setSoftWrapLanguage);
  const clearLanguage = useDocStore((s) => s.clearSoftWrapLanguage);

  const wrapToggleId = useId();
  const codeToggleId = useId();
  const addSelectId = useId();
  const [addKey, setAddKey] = useState<string>('');

  const entries = Object.keys(byLanguage).sort((a, b) =>
    (languageChoice(a)?.label ?? a).localeCompare(languageChoice(b)?.label ?? b),
  );
  const available = LANGUAGE_CHOICES.filter((c) => !(c.key in byLanguage));

  const addException = (): void => {
    if (!addKey) return;
    setLanguage(addKey, true);
    setAddKey('');
  };

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Soft wrap</h4>
          <p>Wrap long lines to the editor width instead of scrolling horizontally.</p>
        </div>
        <div className="mp-settings__row">
          <label htmlFor={wrapToggleId} className="mp-settings__row-label">
            Wrap long lines
          </label>
          <button
            id={wrapToggleId}
            type="button"
            role="switch"
            aria-checked={softWrapDefault}
            className="mp-switch"
            data-on={softWrapDefault ? '' : undefined}
            onClick={() => setSoftWrapDefault(!softWrapDefault)}
          >
            <span className="mp-switch__track" aria-hidden>
              <span className="mp-switch__thumb" />
            </span>
            <span className="mp-switch__label" aria-hidden>
              {softWrapDefault ? 'On' : 'Off'}
            </span>
          </button>
        </div>
        <div className="mp-settings__row">
          <label htmlFor={codeToggleId} className="mp-settings__row-label">
            <span>Don’t wrap code files</span>
            <span className="mp-settings__row-help">
              Keep code (and other non-prose languages) on single long lines even
              when wrapping is on. Markdown and plain text still wrap.
            </span>
          </label>
          <button
            id={codeToggleId}
            type="button"
            role="switch"
            aria-checked={excludeCode}
            className="mp-switch"
            data-on={excludeCode ? '' : undefined}
            onClick={() => setExcludeCode(!excludeCode)}
          >
            <span className="mp-switch__track" aria-hidden>
              <span className="mp-switch__thumb" />
            </span>
            <span className="mp-switch__label" aria-hidden>
              {excludeCode ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Per-language exceptions</h4>
          <p>Force wrapping on or off for specific languages, overriding the rules above.</p>
        </div>

        <div className="mp-settings__row">
          <label htmlFor={addSelectId} className="mp-settings__row-label">
            Add a language
          </label>
          <div className="mp-settings__number">
            <select
              id={addSelectId}
              value={addKey}
              onChange={(e) => setAddKey(e.currentTarget.value)}
            >
              <option value="">Select…</option>
              {available.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button type="button" className="mp-btn" disabled={!addKey} onClick={addException}>
              Add
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="mp-settings__row-help">No per-language exceptions yet.</p>
        ) : (
          <ul className="mp-settings__sortable" role="list">
            {entries.map((key) => {
              const label = languageChoice(key)?.label ?? key;
              const on = byLanguage[key];
              return (
                <li key={key} className="mp-settings__sortable-item">
                  <span className="mp-settings__sortable-name">{label}</span>
                  <span className="mp-settings__sortable-actions">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`Wrap ${label}`}
                      className="mp-switch"
                      data-on={on ? '' : undefined}
                      onClick={() => setLanguage(key, !on)}
                    >
                      <span className="mp-switch__track" aria-hidden>
                        <span className="mp-switch__thumb" />
                      </span>
                      <span className="mp-switch__label" aria-hidden>
                        {on ? 'Wrap' : 'No wrap'}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="mp-settings__sortable-arrow"
                      aria-label={`Remove ${label}`}
                      title={`Remove ${label}`}
                      onClick={() => clearLanguage(key)}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

// ───────── Terminal panel ─────────

const CURSOR_STYLE_LABELS: Record<TerminalCursorStyle, string> = {
  bar: 'Bar',
  block: 'Block',
  underline: 'Underline',
};

/**
 * A small glyph that depicts the cursor shape itself (a filled block, a vertical
 * bar, or a bottom underline) so the choice reads at a glance. Decorative: the
 * accessible name comes from the adjacent text label.
 */
function CursorStyleIcon({ style }: { style: TerminalCursorStyle }): ReactElement {
  return (
    <svg width={18} height={18} viewBox="0 0 20 20" aria-hidden focusable={false}>
      {style === 'block' && (
        <rect x={4} y={3} width={12} height={14} rx={1} fill="currentColor" />
      )}
      {style === 'bar' && (
        <rect x={4} y={3} width={2.6} height={14} rx={1} fill="currentColor" />
      )}
      {style === 'underline' && (
        <rect x={4} y={14.4} width={12} height={2.6} rx={1} fill="currentColor" />
      )}
    </svg>
  );
}

/**
 * Terminal preferences:
 *   - Cursor style (block / bar / underline) for the integrated terminal.
 *   - Shell integration: marks the prompt (OSC 133) so a click moves the shell
 *     cursor where you click. zsh and bash, started from your real config.
 */
function TerminalSettings(_: PanelProps): ReactElement {
  const cursorStyle = useDocStore((s) => s.terminalCursorStyle);
  const setCursorStyle = useDocStore((s) => s.setTerminalCursorStyle);
  const shellIntegration = useDocStore((s) => s.terminalShellIntegration);
  const setShellIntegration = useDocStore((s) => s.setTerminalShellIntegration);

  const toggleId = useId();

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Cursor</h4>
          <p>The shape of the cursor in the integrated terminal.</p>
        </div>
        <div
          className="mp-settings__radio-row"
          role="radiogroup"
          aria-label="Terminal cursor style"
        >
          {TERMINAL_CURSOR_STYLES.map((style) => {
            const selected = cursorStyle === style;
            return (
              <button
                key={style}
                type="button"
                role="radio"
                aria-checked={selected}
                className="mp-settings__radio-card"
                data-active={selected ? '' : undefined}
                onClick={() => setCursorStyle(style)}
              >
                <CursorStyleIcon style={style} />
                <span>{CURSOR_STYLE_LABELS[style]}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Shell integration</h4>
          <p>
            Marks the prompt so clicking moves the cursor where you click, the way
            Warp and similar terminals do. Works with zsh and bash; the shell still
            starts from your own configuration.
          </p>
        </div>
        <div className="mp-settings__row">
          <label htmlFor={toggleId} className="mp-settings__row-label">
            Click to position the cursor
          </label>
          <button
            id={toggleId}
            type="button"
            role="switch"
            aria-checked={shellIntegration}
            className="mp-switch"
            data-on={shellIntegration ? '' : undefined}
            onClick={() => setShellIntegration(!shellIntegration)}
          >
            <span className="mp-switch__track" aria-hidden>
              <span className="mp-switch__thumb" />
            </span>
            <span className="mp-switch__label" aria-hidden>
              {shellIntegration ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * A labelled number input expressed in seconds for the user (we store ms).
 * Buffers the typed value as a string until blur / Enter so partial edits
 * (e.g. clearing the field) don't snap mid-keystroke. Range is enforced on
 * commit by the store setters (these inputs only hint the bounds visually).
 */
function DurationField({
  label,
  help,
  valueMs,
  minMs,
  maxMs,
  onCommit,
}: {
  label: string;
  help: string;
  valueMs: number;
  minMs: number;
  maxMs: number;
  onCommit: (ms: number) => void;
}): ReactElement {
  const fieldId = useId();
  // Show seconds with one decimal of precision (3.0, 2.5, …).
  const toSec = (ms: number): string => (ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1);
  const [draft, setDraft] = useState<string>(() => toSec(valueMs));

  // Keep the input synced when the store value changes externally
  // (e.g. clamp on commit, or another control altering it).
  useEffect(() => {
    setDraft(toSec(valueMs));
  }, [valueMs]);

  const commit = (): void => {
    const num = Number(draft);
    if (!Number.isFinite(num) || num <= 0) {
      setDraft(toSec(valueMs)); // revert
      return;
    }
    onCommit(Math.round(num * 1000));
  };

  return (
    <div className="mp-settings__row">
      <label htmlFor={fieldId} className="mp-settings__row-label">
        <span>{label}</span>
        <span className="mp-settings__row-help">{help}</span>
      </label>
      <div className="mp-settings__number">
        <input
          id={fieldId}
          type="number"
          inputMode="decimal"
          step="0.5"
          min={minMs / 1000}
          max={maxMs / 1000}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(toSec(valueMs));
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
        <span className="mp-settings__number-unit" aria-hidden>s</span>
      </div>
    </div>
  );
}

// ───────── Command Palette panel ─────────

/**
 * Tunes how the Cmd/Ctrl+K palette behaves. These map 1:1 onto the constants
 * the palette used to hard-code (see CommandPalette.tsx) plus a UX preference
 * for the K-shortcut's default mode.
 */
function PaletteSettings(_: PanelProps): ReactElement {
  const mixedCap = useDocStore((s) => s.paletteMixedCap);
  const setMixedCap = useDocStore((s) => s.setPaletteMixedCap);
  const debounceMs = useDocStore((s) => s.paletteWorkspaceDebounceMs);
  const setDebounceMs = useDocStore((s) => s.setPaletteWorkspaceDebounceMs);
  const minQuery = useDocStore((s) => s.paletteMinWorkspaceQuery);
  const setMinQuery = useDocStore((s) => s.setPaletteMinWorkspaceQuery);
  const commandMode = useDocStore((s) => s.paletteCommandModeDefault);
  const setCommandMode = useDocStore((s) => s.setPaletteCommandModeDefault);
  const toggleId = useId();

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Result limits</h4>
          <p>How many rows the palette shows in mixed-search mode (no prefix).</p>
        </div>
        <IntegerField
          label="Mixed-results cap"
          help="Maximum rows rendered when searching across commands, tabs, and workspace."
          unit="rows"
          value={mixedCap}
          min={PALETTE_MIXED_CAP_MIN}
          max={PALETTE_MIXED_CAP_MAX}
          step={5}
          onCommit={setMixedCap}
        />
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Workspace search</h4>
          <p>The async file scan that backs mixed-mode results.</p>
        </div>
        <IntegerField
          label="Debounce"
          help="Wait this long after the last keystroke before searching the workspace."
          unit="ms"
          value={debounceMs}
          min={PALETTE_WORKSPACE_DEBOUNCE_MIN_MS}
          max={PALETTE_WORKSPACE_DEBOUNCE_MAX_MS}
          step={25}
          onCommit={setDebounceMs}
        />
        <IntegerField
          label="Minimum query length"
          help="Don't search the workspace until the query has at least this many characters."
          unit="chars"
          value={minQuery}
          min={PALETTE_MIN_WORKSPACE_QUERY_MIN}
          max={PALETTE_MIN_WORKSPACE_QUERY_MAX}
          step={1}
          onCommit={setMinQuery}
        />
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Result priority</h4>
          <p>
            Drag to reorder how mixed-mode results stack up. The top row is the
            highest-priority bucket.
          </p>
        </div>
        <PrioritySortable />
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Default mode</h4>
          <p>How the palette opens when you press its main shortcut.</p>
        </div>
        <div className="mp-settings__row">
          <label htmlFor={toggleId} className="mp-settings__row-label">
            <span>Open palette in command mode</span>
            <span className="mp-settings__row-help">
              When on, Cmd/Ctrl+K opens the palette already pre-seeded with “/”.
            </span>
          </label>
          <button
            id={toggleId}
            type="button"
            role="switch"
            aria-checked={commandMode}
            className="mp-switch"
            data-on={commandMode ? '' : undefined}
            onClick={() => setCommandMode(!commandMode)}
          >
            <span className="mp-switch__track" aria-hidden>
              <span className="mp-switch__thumb" />
            </span>
            <span className="mp-switch__label" aria-hidden>
              {commandMode ? 'On' : 'Off'}
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * Drag-sortable list of the seven mixed-mode result buckets. Each row is
 * `draggable` (HTML5 native — no extra deps); Up/Down chevrons mirror the
 * drag for keyboard users; a Reset button restores the default order.
 */
function PrioritySortable(): ReactElement {
  const order = useDocStore((s) => s.paletteMixedOrder);
  const setOrder = useDocStore((s) => s.setPaletteMixedOrder);
  const reset = useDocStore((s) => s.resetPaletteMixedOrder);
  const [dragSrc, setDragSrc] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const move = (from: number, to: number): void => {
    if (from === to || to < 0 || to >= order.length) return;
    const next = [...order];
    const [taken] = next.splice(from, 1);
    next.splice(to, 0, taken);
    setOrder(next);
  };

  const onDrop = (target: number): void => {
    if (dragSrc !== null) move(dragSrc, target);
    setDragSrc(null);
    setDragOver(null);
  };

  return (
    <div className="mp-settings__sortable-wrap">
      <ol className="mp-settings__sortable" role="list">
        {order.map((bucket, i) => (
          <li
            key={bucket}
            className="mp-settings__sortable-item"
            draggable
            data-dragging={dragSrc === i ? '' : undefined}
            data-drag-over={dragOver === i && dragSrc !== i ? '' : undefined}
            onDragStart={(e) => {
              setDragSrc(i);
              e.dataTransfer.effectAllowed = 'move';
              // Firefox requires SOMETHING on the dataTransfer or drag never fires.
              e.dataTransfer.setData('text/plain', bucket);
            }}
            onDragEnter={() => setDragOver(i)}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(i);
            }}
            onDragEnd={() => {
              setDragSrc(null);
              setDragOver(null);
            }}
          >
            <span className="mp-settings__sortable-grip" aria-hidden>
              <GripVertical size={14} />
            </span>
            <span className="mp-settings__sortable-rank">{i + 1}</span>
            <span className="mp-settings__sortable-name">{bucket}</span>
            <span className="mp-settings__sortable-actions">
              <button
                type="button"
                className="mp-settings__sortable-arrow"
                aria-label={`Move "${bucket}" up`}
                title={`Move "${bucket}" up`}
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
              >
                <ArrowUp size={14} aria-hidden />
              </button>
              <button
                type="button"
                className="mp-settings__sortable-arrow"
                aria-label={`Move "${bucket}" down`}
                title={`Move "${bucket}" down`}
                disabled={i === order.length - 1}
                onClick={() => move(i, i + 1)}
              >
                <ArrowDown size={14} aria-hidden />
              </button>
            </span>
          </li>
        ))}
      </ol>
      <button type="button" className="mp-btn mp-settings__sortable-reset" onClick={reset}>
        <RotateCcw size={13} aria-hidden />
        Reset to default
      </button>
    </div>
  );
}

/**
 * Integer number input with a buffered draft (mirrors DurationField above). The
 * store setter clamps on commit; the min/max here are visual hints + native
 * spinner bounds. Empty/non-numeric drafts revert on blur.
 */
function IntegerField({
  label,
  help,
  unit,
  value,
  min,
  max,
  step,
  onCommit,
}: {
  label: string;
  help: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (n: number) => void;
}): ReactElement {
  const fieldId = useId();
  const [draft, setDraft] = useState<string>(() => String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (): void => {
    const num = Number(draft);
    if (!Number.isFinite(num)) {
      setDraft(String(value));
      return;
    }
    onCommit(Math.round(num));
  };

  return (
    <div className="mp-settings__row">
      <label htmlFor={fieldId} className="mp-settings__row-label">
        <span>{label}</span>
        <span className="mp-settings__row-help">{help}</span>
      </label>
      <div className="mp-settings__number">
        <input
          id={fieldId}
          type="number"
          inputMode="numeric"
          step={step}
          min={min}
          max={max}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(String(value));
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
        <span className="mp-settings__number-unit" aria-hidden>{unit}</span>
      </div>
    </div>
  );
}

// ───────── About panel (web only) ─────────

const WELOIN_URL = 'https://weloin.com';
const PRODUCT_URL = 'https://slateeditor.com';

/**
 * Inline product info shown on the web. The desktop has an OS-menu About
 * (with full Acknowledgements via AboutDialog); the web has no menu, so we
 * surface the same essential identity here: product name, tagline, version,
 * and outbound links.
 *
 * Version is fetched once lazily — the web build resolves it from package.json
 * at build time (see WebPlatform.getAppVersion). Falls back to "—" when no
 * platform is wired (test renders, etc.).
 */
function AboutPanel({ platform }: PanelProps): ReactElement {
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!platform || typeof platform.getAppVersion !== 'function') return;
    let cancelled = false;
    platform
      .getAppVersion()
      .then((v) => {
        if (!cancelled) setVersion(v);
      })
      .catch(() => {
        /* swallow; the panel just shows "—" */
      });
    return () => {
      cancelled = true;
    };
  }, [platform]);

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Slate</h4>
          <p>Fast, native, cross-platform Markdown viewer/editor with three synchronized views.</p>
        </div>
        <div className="mp-settings__row">
          <span className="mp-settings__row-label">Version</span>
          <span className="mp-settings__row-value">{version ?? '—'}</span>
        </div>
        <div className="mp-settings__row">
          <span className="mp-settings__row-label">Channel</span>
          <span className="mp-settings__row-value">Web (always latest)</span>
        </div>
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Links</h4>
        </div>
        <div className="mp-settings__row">
          <span className="mp-settings__row-label">Product</span>
          <a
            href={PRODUCT_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="mp-settings__row-value mp-settings__link"
          >
            slateeditor.com
          </a>
        </div>
        <div className="mp-settings__row">
          <span className="mp-settings__row-label">Built by</span>
          <a
            href={WELOIN_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="mp-settings__row-value mp-settings__link"
          >
            Weloin
          </a>
        </div>
      </section>
    </div>
  );
}

// ───────── Plugins panel ─────────

/**
 * Standing per-plugin permissions transparency (spec 7.6). Resolves the one app
 * plugin-system singleton from the platform and renders the detailed permissions
 * panel. When no platform is wired (SSR / bare test renders), it degrades to a
 * short notice rather than crashing.
 */
function PluginsSettingsPanel({ platform }: PanelProps): ReactElement {
  if (!platform) {
    return (
      <div className="mp-settings__sections">
        <section className="mp-settings__section">
          <div className="mp-settings__section-head">
            <h4>Plugins</h4>
            <p>Plugin permissions are unavailable in this context.</p>
          </div>
        </section>
      </div>
    );
  }
  return <PluginsSettings system={getAppPluginSystem(platform)} />;
}

// ───────── Updates panel ─────────

/**
 * In-app updater controls. Mirrors the five updateStore statuses (idle,
 * checking, upToDate, available, error) plus a "no controller attached"
 * fallback for the web build, where no Tauri updater is wired in.
 *
 * The store owns all transitions; this panel only renders state and fires
 * `checkNow` / `requestInstall`. The install path needs a PlatformBridge it
 * can't see from here, so `requestInstall` is handed the `{} as never` stand-in
 * and the App shell (listening on the gate event) owns the real bridge.
 */
function UpdatesSettings(_: PanelProps): ReactElement {
  const status = useUpdateStore((s) => s.status);
  const currentVersion = useUpdateStore((s) => s.currentVersion);
  const latestVersion = useUpdateStore((s) => s.latestVersion);
  const lastCheckedAt = useUpdateStore((s) => s.lastCheckedAt);
  const error = useUpdateStore((s) => s.error);
  const controllerAttached = useUpdateStore((s) => s.controllerAttached);

  if (!controllerAttached) {
    return (
      <div className="mp-settings__sections">
        <section className="mp-settings__section">
          <div className="mp-settings__section-head">
            <h4>Updates</h4>
            <p>
              Automatic updates aren't available in this build. Visit{' '}
              <strong>slateeditor.com</strong> to download the desktop app.
            </p>
          </div>
        </section>
      </div>
    );
  }

  const checkNow = (): void => {
    void useUpdateStore.getState().checkNow({ mode: 'manual' });
  };
  const updateNow = (): void => {
    // The bridge is passed as the empty stand-in here because requestInstall
    // doesn't need it on the gate path (the App owns the bridge for the
    // actual install). The App listens on `slate:open-update-gate`.
    void useUpdateStore.getState().requestInstall({} as never);
  };

  return (
    <div className="mp-settings__sections">
      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Current version</h4>
          <p>The version of Slate installed on this machine.</p>
        </div>
        <div className="mp-settings__row">
          <span className="mp-settings__row-label">Slate</span>
          <code className="mp-settings__version">
            {currentVersion ? `v${currentVersion}` : 'unknown'}
          </code>
        </div>
      </section>

      <section className="mp-settings__section">
        <div className="mp-settings__section-head">
          <h4>Check for updates</h4>
          <p>Query the update server for a newer version.</p>
        </div>

        {status === 'checking' && (
          <p className="mp-settings__row-help">Checking…</p>
        )}

        {status === 'upToDate' && (
          <p className="mp-settings__row-help">
            You&rsquo;re on the latest version.{' '}
            <button type="button" className="mp-link" onClick={checkNow}>
              Check again
            </button>
          </p>
        )}

        {status === 'available' && (
          <div className="mp-settings__row">
            <span className="mp-settings__row-label">
              Update available: <strong>v{latestVersion}</strong>
            </span>
            <button
              type="button"
              className="mp-btn mp-btn--primary"
              onClick={updateNow}
            >
              <Sparkles size={13} aria-hidden /> Update now
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="mp-settings__row">
            <span className="mp-settings__row-label">
              Couldn&rsquo;t check for updates{error ? `: ${error}` : '.'}
            </span>
            <button type="button" className="mp-btn" onClick={checkNow}>
              <RefreshCcw size={13} aria-hidden /> Retry
            </button>
          </div>
        )}

        {(status === 'idle' || status === 'installing') && (
          <div className="mp-settings__row">
            <span className="mp-settings__row-label">&nbsp;</span>
            <button
              type="button"
              className="mp-btn"
              onClick={checkNow}
              disabled={status === 'installing'}
            >
              <RefreshCcw size={13} aria-hidden /> Check for updates
            </button>
          </div>
        )}

        {lastCheckedAt && (
          <p className="mp-settings__row-help">
            Last checked: {formatLastChecked(lastCheckedAt)}
          </p>
        )}
      </section>
    </div>
  );
}

function formatLastChecked(epochMs: number): string {
  const ageMs = Date.now() - epochMs;
  if (ageMs < 60_000) return 'just now';
  if (ageMs < 60 * 60_000) {
    const m = Math.round(ageMs / 60_000);
    return `${m} min ago`;
  }
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
